import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import {
  currencyOption,
  generateDaysArray,
  RealStateTransaction,
} from "../../../../../utility/data";
import {
  addTransaction,
  getUserAssets,
  updateTransaction,
} from "../../../../../services/transaction";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber } from "../../../../../utility/helper";
import {
  fetchGroups,
  resetGroupsState,
} from "../../../../../redux/slices/groupsSlice";
import {
  FormActions,
  FormDatePicker,
  FormDropdown,
  FormInput,
  ToggleSwitch,
} from "../../../../../components/Stepper/fields/inputField";
import { useNavigate } from "react-router-dom";
import { handleErrorRedirect } from "../../../../../utility/errorRedirect";

const CreateTransactionModal = ({
  visible,
  singleTransactionData,
  actionTypes,
  fetchTransactionData,
  fetchFutureTransactionData,
  GetstatsData,
  GetRoiData,
  statsData,
  viewTransaction,
  onHide,
  SelectType,
  loading,
  setLoading,
  toast,
  assetName,
}) => {
  const { groupsData, groupLoading } = useSelector((state) => state.groups);
  const { currency } = useSelector((state) => state.user);
  const InitialDateValue = viewTransaction?.transactions?.find(
    (item) => item.is_initial
  );
  const filteredData = groupsData?.filter(
    (group) => group?.id == assetName?.included_in
  );
  const accessToken = JSON.parse(localStorage.getItem("access_token"));

  const initialTransactionData = {
    assetType: RealStateTransaction[0],
    includeIn: filteredData[0] ? filteredData[0] : groupsData[0],
    date: moment().toDate(),
    type: singleTransactionData?.type
      ? singleTransactionData?.type
      : SelectType,
    multiples: 0,
    currency: currency ? currency : currencyOption[0].value,
    reason: singleTransactionData?.remark ? singleTransactionData?.remark : "",
    remark: singleTransactionData?.remark ? singleTransactionData?.remark : "",
    EBITDA: 0,
    noi: 0,
    capRate: 0,
    value: singleTransactionData.value
      ? formatNumber(singleTransactionData.value)
      : 0,
    p_and_l: singleTransactionData.p_and_l
      ? formatNumber(singleTransactionData.p_and_l)
      : 0,
    isRecurring: singleTransactionData.is_recurring
      ? singleTransactionData.is_recurring
      : false,
    recurringDay: singleTransactionData.recurring_day
      ? singleTransactionData.recurring_day
      : 0,
    cashAssets: singleTransactionData.cash_asset_id
      ? singleTransactionData.cash_asset_id
      : "",
  };

  const [createTransactionData, setCreateTransactionData] = useState(
    initialTransactionData
  );
  const [errors, setErrors] = useState({});
  const [recurringDays, setRecurringDays] = useState([]);
  const [userAssets, setUserAssets] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (singleTransactionData) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        currency: singleTransactionData.currency || prevData.currency,
        date: singleTransactionData.date
          ? moment(singleTransactionData.date).toDate()
          : prevData.date,
        reason: singleTransactionData.remark || "",
        value: formatNumber(singleTransactionData.value) || "",
        p_and_l: formatNumber(singleTransactionData.p_and_l) || "",
        type: singleTransactionData.type || "",
        cashAssets: singleTransactionData.cash_asset_id || "",
        isRecurring: singleTransactionData.is_recurring || false,
        recurringDay: singleTransactionData.recurring_day || 0,
      }));
    }
  }, [singleTransactionData]);

  useEffect(() => {
    if (singleTransactionData && userAssets.length > 0) {
      const selectedAsset = userAssets.find(
        (asset) => asset?.id === singleTransactionData?.cash_asset_id
      );

      setCreateTransactionData((prevData) => ({
        ...prevData,
        cashAssets: selectedAsset ? selectedAsset.id : prevData.cashAssets,
      }));
    }
  }, [singleTransactionData, userAssets]);

  useEffect(() => {
    if (currency && currency !== createTransactionData.currency) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        currency,
      }));
    }
  }, [currency]);

  useEffect(() => {
    GetUserAssetsData();
  }, []);

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchGroups(accessToken)).then((resultAction) => {
        if (fetchGroups.rejected.match(resultAction)) {
          const status = resultAction?.payload?.status;
          handleErrorRedirect(status, navigate);
        }
      });
    }

    return () => {
      dispatch(resetGroupsState());
    };
  }, [dispatch, accessToken, navigate]);

  useEffect(() => {
    setCreateTransactionData((prevData) => ({
      ...prevData,
      type: singleTransactionData?.type ?? SelectType,
    }));
  }, [SelectType, visible]);

  useEffect(() => {
    if (groupsData.length > 0) {
      setCreateTransactionData((prev) => ({
        ...prev,
        includeIn: filteredData[0] ? filteredData[0] : groupsData[0],
      }));
    }
  }, [groupsData, assetName]);

  useEffect(() => {
    if (createTransactionData.isRecurring) {
      const days = generateDaysArray(createTransactionData.date);
      setRecurringDays(days);

      if (createTransactionData.recurring > days.length) {
        handleInputChange({ name: "recurring", value: days.length });
      }
    }
  }, [createTransactionData.date, createTransactionData.isRecurring]);

  const GetUserAssetsData = async () => {
    setLoading(true);
    try {
      const response = await getUserAssets(1, accessToken);
      if (response.status === 200) {
        const data = response?.data.data;
        setUserAssets(data);
      } else {
        throw new Error("Failed to fetch user data");
      }
    } catch (err) {
      const status = err?.response?.status;
      handleErrorRedirect(status, navigate);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target || e;

    setCreateTransactionData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name] && value && value.toString().trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handlePLChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    // Allow only numbers, a single decimal point, and an optional leading '-'
    let rawValue = value.replace(/[^0-9.-]/g, "");

    if (rawValue === "") {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        p_and_l: "",
      }));
      return;
    }

    // Ensure '-' is only at the start
    if (rawValue.includes("-")) {
      // Keep only the first '-' if it's at the start
      rawValue = rawValue.startsWith("-")
        ? "-" + rawValue.slice(1).replace(/-/g, "")
        : rawValue.replace(/-/g, ""); // remove any misplaced '-'
    }

    if (rawValue.length > 12) {
      rawValue = rawValue.slice(0, 12);
    }

    // Prevent multiple decimal points
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }
    value = formatNumber(rawValue);

    // Update state
    setCreateTransactionData((prevData) => ({
      ...prevData,
      p_and_l: value,
    }));
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (value.trim()) {
        delete updatedErrors[name];
      } else {
        updatedErrors[name] = "P&L is required";
      }
      return updatedErrors;
    });
  };

  const handleValueChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    let rawValue = value.replace(/[^0-9.,]/g, "").replace(/[,']/g, "");

    if (rawValue === "" || isNaN(rawValue)) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        value: "",
      }));
      return;
    }

    if (rawValue.length > 12) {
      rawValue = rawValue.slice(0, 12);
    }

    // Prevent multiple decimals
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // Format value
    value = formatNumber(rawValue);

    let AutoCalPL = null;
    if (SelectType === "VALUATION" || SelectType === "SELL") {
      AutoCalPL = formatNumber(
        Number(rawValue) - Number(statsData?.initial_value)
      );
    }

    setCreateTransactionData((prevData) => ({
      ...prevData,
      value: value,
      ...(AutoCalPL !== null && { p_and_l: AutoCalPL }),
    }));

    // Remove errors if input is valid
    if (errors[name] && value.trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handleAssetTypeChange = (e) => {
    const selectedAssetType = e.value;

    setCreateTransactionData((prevData) => ({
      ...prevData,
      assetType: selectedAssetType,
    }));
  };

  const handleCashAssetChange = (e) => {
    setCreateTransactionData((prevData) => ({
      ...prevData,
      cashAssets: e.value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!createTransactionData.date) {
      newErrors.date = "Date is required";
    }
    if (!createTransactionData.type) {
      newErrors.type = "Type is required";
    }
    if (!createTransactionData.currency) {
      newErrors.currency = "Currency is required";
    }

    if (!createTransactionData.includeIn) {
      newErrors.includeIn = "groups is required";
    }
    if (!createTransactionData.value) {
      newErrors.value = "value is required";
    }

    if (
      !createTransactionData.p_and_l &&
      (SelectType == "VALUATION" || SelectType == "SELL")
    ) {
      newErrors.p_and_l = "P&L is required";
    }

    if (
      (createTransactionData.type == "IN" ||
        createTransactionData.type == "OUT") &&
      !createTransactionData.value
    ) {
      newErrors.value = "Value is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      setLoading(true);
      if (!assetName) {
        setLoading(false);
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: "Please add the first initial transaction",
          life: 3000,
        });
        return;
      }

      const formattedData = {
        ...createTransactionData,
        p_and_l: createTransactionData.p_and_l
          ? createTransactionData.p_and_l.replace(/[,']/g, "")
          : "",
        value: createTransactionData.value
          ? createTransactionData.value.replace(/[,']/g, "")
          : "",
        type: createTransactionData.type ? createTransactionData.type : "",
        date: createTransactionData.date
          ? moment(createTransactionData.date).format("YYYY-MM-DD HH:mm:ss")
          : null,
      };

      try {
        const response = await (actionTypes === "Create"
          ? addTransaction(formattedData, assetName, accessToken)
          : updateTransaction(
              singleTransactionData.id,
              formattedData,
              assetName,
              accessToken
            ));

        if (response?.data?.status === true) {
          fetchTransactionData();
          fetchFutureTransactionData();
          GetstatsData();
          GetRoiData();
          handleClose();
          toast.current.show({
            className: "bg-green-300 text-white",
            severity: "success",
            summary: "Success",
            detail: response?.data?.message,
            life: 3000,
          });
        }
      } catch (error) {
        const status = error?.response?.status;
        handleErrorRedirect(status, navigate);
        console.error("Error adding transaction:", error);
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: error?.message,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!visible) return null;

  const handleClose = () => {
    setCreateTransactionData(initialTransactionData);
    setErrors({});
    onHide();
  };

  const shouldShowRecurringToggle =
    (actionTypes === "Create" ||
      (actionTypes === "Update" && singleTransactionData?.is_type_recurring)) &&
    SelectType === "INCOME";

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 px-4 md:px-0">
      <div className="dark:bg-custom-radial bg-[#F6F6F6] md:w-2/3 xl:w-1/2 w-full shadow-lg p-6 max-h-[calc(100%-30%)] overflow-y-auto rounded-lg border border-lightPrimary/40 dark:border-info/40">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl text-lightPrimary dark:text-primary-gradient font-semibold">
            {actionTypes} Transaction
          </h2>
          <FaTimes
            onClick={handleClose}
            className="cursor-pointer text-lightPrimary dark:text-darkWaring"
            fontSize={20}
          />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <FormDropdown
              label="Asset Type"
              name="assetType"
              value={createTransactionData.assetType}
              onChange={handleAssetTypeChange}
              options={RealStateTransaction}
              optionsLabel="name"
              disabled
              error={errors.assetType}
              className={`rounded-lg w-full p-0 bg-transparent border  border-info dark:border-gray-300 text-white focus:outline-none focus:border-b-2 ${
                createTransactionData.assetType ? "asset_Dropdown" : ""
              }`}
              placeholder="Select Asset Type"
            />
            <FormDropdown
              label="Include In*"
              name="includeIn"
              value={createTransactionData.includeIn}
              onChange={(e) =>
                handleInputChange({ name: "includeIn", value: e.value })
              }
              options={groupsData}
              disabled
              error={errors.includeIn}
              optionsLabel="name"
              className={`w-full p-0 bg-transparent border  border-info dark:border-gray-300 text-white rounded-lg focus:outline-none focus:border-b-2 ${
                createTransactionData.includeIn ? "asset_Dropdown" : ""
              }`}
              placeholder="Select Include In"
            />

            <FormDatePicker
              label="Date*"
              name="date"
              value={createTransactionData.date}
              onChange={(e) =>
                handleInputChange({ name: "date", value: e.value })
              }
              error={errors.date}
              maxDate={moment().toDate()}
              minDate={
                InitialDateValue?.date
                  ? moment(InitialDateValue.date).toDate()
                  : moment().toDate()
              }
              disabled={actionTypes === "Update"}
            />
            <FormInput
              label="Type*"
              name="type"
              value={createTransactionData.type}
              onChange={handleInputChange}
              error={errors.type}
              disabled={true}
            />

            <FormInput
              label="Reason"
              name="reason"
              value={createTransactionData.reason}
              onChange={handleInputChange}
              error={errors.reason}
            />

            <FormDropdown
              label="Currency*"
              name="currency"
              value={createTransactionData.currency}
              onChange={handleInputChange}
              disabled
              options={currencyOption}
              error={errors.currency}
              optionsLabel="label"
              optionsValue="value"
              className={`create_transaction w-full p-0 bg-transparent border-b  border-info dark:border-gray-300 text-white rounded-none focus:outline-none focus:border-b-2 ${
                createTransactionData.currency ? "asset_Dropdown" : ""
              }`}
              placeholder="Select a Currency"
            />

            <FormInput
              label="Value*"
              name="value"
              value={createTransactionData.value || ""}
              onChange={handleValueChange}
              error={errors.value}
            />
            {(SelectType === "VALUATION" || SelectType === "SELL") && (
              <FormInput
                label="P&L*"
                name="p_and_l"
                value={createTransactionData.p_and_l || ""}
                onChange={handlePLChange}
                error={errors.p_and_l}
              />
            )}
            {shouldShowRecurringToggle && (
              <ToggleSwitch
                label="Recurring"
                name="isRecurring"
                checked={createTransactionData.isRecurring}
                onChange={(e) =>
                  handleInputChange({
                    name: "isRecurring",
                    value: e.target.checked,
                  })
                }
              />
            )}

            {SelectType === "SELL" && (
              <FormDropdown
                label="Cash Assets"
                name="cashAssets"
                showClear={!!createTransactionData.cashAssets}
                value={createTransactionData.cashAssets}
                onChange={handleCashAssetChange}
                options={userAssets}
                error={errors.cashAssets}
                optionsLabel="name"
                optionsValue="id"
                className={`create_transaction w-full p-0 bg-transparent border-b  border-info dark:border-gray-300 text-white rounded-none focus:outline-none focus:border-b-2 ${
                  createTransactionData.cashAssets ? "asset_Dropdown" : ""
                }`}
                placeholder="Select Cash Assets"
              />
            )}

            {createTransactionData.isRecurring && (
              <FormDropdown
                label="Recurring Day*"
                name="recurringDay"
                value={createTransactionData.recurringDay}
                onChange={(e) =>
                  handleInputChange({
                    name: "recurringDay",
                    value: e.value,
                  })
                }
                options={recurringDays}
                error={errors.recurringDay}
                optionsLabel="name"
                optionsValue="value"
                className={`w-full p-0 bg-transparent border border-gray-300 text-white rounded-lg focus:outline-none focus:border-b-2 ${
                  createTransactionData.recurringDay ? "asset_Dropdown" : ""
                }`}
                placeholder="Select Recurring Day"
              />
            )}
          </div>
          <FormActions
            loading={loading}
            onCancel={handleClose}
            actionType={actionTypes}
            submitType="submit"
          />
        </form>
      </div>
    </div>
  );
};

export default CreateTransactionModal;
